import { Helmet } from "react-helmet-async";

const BASE_URL = "https://www.emoose.de";

interface SEOHeadProps {
  title: string;
  description: string;
  path?: string;
  type?: "website" | "article";
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  ogImage?: string;
  breadcrumbs?: { name: string; path: string }[];
  rssUrl?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
  };
}

const OG_IMAGE_URL = "https://www.emoose.de/og-image.png";

export function SEOHead({
  title,
  description,
  path = "",
  type = "website",
  noindex = false,
  jsonLd,
  ogImage,
  breadcrumbs,
  rssUrl,
  article,
}: SEOHeadProps) {
  const fullTitle = title.includes("emoose") ? title : `${title} | emoose GmbH`;
  const canonical = `${BASE_URL}${path}`;
  const truncatedDescription = description.length > 160 ? description.slice(0, 157) + "…" : description;
  const image = ogImage || OG_IMAGE_URL;

  const breadcrumbJsonLd = breadcrumbs
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Startseite", item: `${BASE_URL}/` },
          ...breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            position: i + 2,
            name: b.name,
            item: `${BASE_URL}${b.path}`,
          })),
        ],
      }
    : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={truncatedDescription} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {rssUrl && (
        <link rel="alternate" type="application/rss+xml" title="emoose Blog RSS Feed" href={rssUrl} />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={truncatedDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="emoose GmbH" />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Article meta */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article?.author && (
        <meta property="article:author" content={article.author} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={truncatedDescription} />
      <meta name="twitter:image" content={image} />

      {/* Organization JSON-LD (every page) */}
      <script type="application/ld+json">
        {JSON.stringify(organizationJsonLd)}
      </script>

      {/* JSON-LD */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      )}
    </Helmet>
  );
}
/** Organization JSON-LD (used on every page via Layout) */
export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://www.emoose.de/#organization",
  name: "emoose GmbH",
  legalName: "emoose GmbH",
  url: "https://www.emoose.de",
  logo: "https://www.emoose.de/og-image.png",
  description:
    "Professioneller Fulfillment-Dienstleister für E-Commerce – Versand, Lagerung, Pre-FBA und Lohnverpackung aus Saarbrücken.",
  telephone: "+4968198573774",
  email: "info@emoose.de",
  foundingDate: "2014",
  numberOfEmployees: { "@type": "QuantitativeValue", minValue: 10, maxValue: 50 },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    bestRating: "5",
    worstRating: "1",
    ratingCount: "35",
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "In den Hallen 9/13",
    postalCode: "66115",
    addressLocality: "Saarbrücken",
    addressRegion: "Saarland",
    addressCountry: "DE",
  },
  areaServed: [
    { "@type": "Country", name: "Deutschland" },
    { "@type": "Country", name: "Österreich" },
    { "@type": "Country", name: "Schweiz" },
  ],
  sameAs: [
    "https://www.linkedin.com/company/emoose",
    "https://www.google.com/maps/place/emoose+GmbH",
    "https://www.northdata.de/emoose+GmbH,+Saarbr%C3%BCcken/HRB+104441",
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Fulfillment-Services",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "E-Commerce Fulfillment" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Amazon Pre-FBA" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Seller Fulfilled Prime" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Lohnverpackung" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Retourenmanagement" } },
    ],
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+4968198573774",
    contactType: "customer service",
    availableLanguage: ["German", "English"],
  },
  knowsAbout: [
    "E-Commerce Fulfillment",
    "Amazon FBA Prep",
    "Seller Fulfilled Prime",
    "Lohnverpackung",
    "Retourenmanagement",
    "Aktionsversand",
    "Lagerhaltung",
    "Versandlogistik",
  ],
};

/** LocalBusiness JSON-LD (homepage) */
export const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://www.emoose.de/#localbusiness",
  name: "emoose GmbH",
  description:
    "Professioneller Fulfillment-Dienstleister für E-Commerce – Versand, Lagerung, Pre-FBA & Lohnverpackung aus Saarbrücken.",
  url: "https://www.emoose.de",
  telephone: "+4968198573774",
  email: "info@emoose.de",
  image: "https://www.emoose.de/og-image.png",
  priceRange: "€€",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    bestRating: "5",
    worstRating: "1",
    ratingCount: "35",
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "In den Hallen 9/13",
    postalCode: "66115",
    addressLocality: "Saarbrücken",
    addressCountry: "DE",
  },
  areaServed: { "@type": "Country", name: "Deutschland" },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday"],
      opens: "10:00",
      closes: "12:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday"],
      opens: "13:00",
      closes: "15:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Friday"],
      opens: "10:00",
      closes: "12:00",
    },
  ],
};

/** WebSite JSON-LD with SearchAction (homepage) */
export const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://www.emoose.de/#website",
  name: "emoose GmbH",
  url: "https://www.emoose.de",
  description: "E-Commerce Fulfillment, Versand & Logistik aus Saarbrücken.",
  publisher: { "@id": "https://www.emoose.de/#organization" },
  inLanguage: "de-DE",
};

/** Reusable JSON-LD for FAQPage */
export function buildFaqJsonLd(faqs: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

/** Reusable JSON-LD for a Service page */
export function buildServiceJsonLd(service: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `https://www.emoose.de${service.url}#service`,
    name: service.name,
    description: service.description,
    url: `https://www.emoose.de${service.url}`,
    provider: { "@id": "https://www.emoose.de/#organization" },
    areaServed: [
      { "@type": "Country", name: "Deutschland" },
      { "@type": "Country", name: "Österreich" },
      { "@type": "Country", name: "Schweiz" },
    ],
    serviceType: "E-Commerce Fulfillment",
  };
}

/** Reusable JSON-LD for HowTo (process pages) */
export function buildHowToJsonLd(howTo: {
  name: string;
  description: string;
  steps: { name: string; text: string; url?: string }[];
  totalTime?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: howTo.name,
    description: howTo.description,
    ...(howTo.totalTime ? { totalTime: howTo.totalTime } : {}),
    step: howTo.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      ...(s.url ? { url: `https://www.emoose.de${s.url}` } : {}),
    })),
  };
}
